import * as React from 'react';

import { NavigateFunction } from 'react-router-dom';

import { FormikProps } from 'formik';
import { MentionProps } from 'antd/es/mentions';

import { List } from '@common/typescript/objects/List';
import { BaseUser } from '@common/react/objects/BaseUser';
import { BaseUserWithAvatarWithDeleted } from '@common/typescript/objects/BaseUser';
import { Nullable } from '@common/typescript/objects/Nullable';
import { File, FileInterface } from '@common/typescript/objects/FileInterface';
import { ButtonsProps, ChatFormComponentState } from '@common/react/components/Chat/ChatMessageForm';
import { Notification } from '@common/typescript/objects/Notification';
import { ApplicationStateWithChats, ChatActions, ChatsActionCreators } from '@common/react/components/Chat/Store/Chats';
import { HandleChangeDelegate } from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import { BaseParams } from '@common/react/objects/BaseParams';

export enum ChatMessageType {
	Regular = 0,
	VoiceMessage = 1,
	Email = 2,
	PhoneCall = 3,
	VideoChat = 4,
	VideoMessage = 5,
	Sticker = 6,
	Giphy = 7,
	Tenor = 8,
	ForwardedMessage = 9,
	ReplyMessage = 10,
	UserAttached = 11,
	UserDetached = 12,
}

export enum ChatRoomType {
	Video,
	Audio
}

export enum ChatKind {
	Personal = 0,
	Conference = 1,
	Group = 2,
	Help = 3,
	Sos = 4,
	Bot = 5,
}

export interface Chat<TUser extends BaseChatUser = BaseChatUser> {
	id: number;
	kind: ChatKind;
	time: number;
	name: string;
	contacts: Array<TUser>;
	contactsIds: Array<number>;
	messages: List<ChatMessage>;
	lastMessage: Nullable<ChatMessage>;
	unviewedMessagesCount: number;
	userId: number;
	archive?: boolean;
}

export enum ChatPlugins {
	Regular = 0,
	VoiceMessage = 1,
	Email = 2,
	PhoneCall = 3,
	VideoChat = 4,
	VideoMessage = 5,
	Sticker = 6,
	Giphy = 7,
	Tenor = 8,
	Forward = 9,
	Reply = 10,
	UserAttached = 11,
	UserDetached = 12,
	Edit = 16,
	Deleted = 17,
	VoiceCall = 18,
	Typing = 26,
	Files = 27,
	Emoji = 28,
	Mentions = 29,
	Reactions = 30,
	LinkPreview = 31,
	LinkPreviewGroup = 32,
	ColorBox = 33,
	Searching = 34,
	Archive = 35,
	AddChat = 36,
	OnlineFilter = 37,
	AllChats = 38,
	CompleteChat = 39,
	LeaveChat = 40,
	ChatNameEditor = 41,
	Copy = 42,
	ModalPlugin = 44,
	Template = 45,
	AdaptiveCards = 46,
	SendLater = 47,
	ReadAllMessages = 48,
}

export interface ChatSettingsChatPageSettings {
	path: string;
	chatIdUrlKey: string;
	archive: string;
}

export interface ChatPlugin<
	TUser extends BaseChatUser = BaseChatUser,
	TChatMessage extends ChatMessage<TUser> = ChatMessage<TUser>,
	TChat extends Chat<TUser> = Chat<TUser>,
> {
	formButton?: (formikBag: FormikProps<NewMessage>, props: ChatFormButtonsProps) => React.ReactNode;
	formComponent?: (props: MentionProps) => React.ReactNode;
	messageControlWrapper?: (options: { chat: TChat, render: (onKeyDown: (e: React.KeyboardEvent) => void) => React.ReactNode }) => React.ReactNode;
	listComponent?: (props: { chat: TChat | null, links: Array<string>, linkPreviewId: string }) => React.ReactNode;
	additionalComponent?: (props: {
		chat: TChat | null,
		defaultIdx: number | null,
		setImg: React.Dispatch<React.SetStateAction<File | undefined>>,
		files: Array<File>,
		basePath: string,
	}) => React.ReactNode;
	message?: {
		render: (options: {
			message: TChatMessage | null,
			contacts: Array<TUser>,
			withRemoteId: boolean | undefined,
			onMouseEnter?: (message: TChatMessage) => void,
			lastVideoCallId: number | undefined,
			onImageClick?: (e: React.MouseEventHandler<HTMLImageElement>, file: File) => void,
		}) => React.ReactNode;
		lastMessage?: (options: { message: TChatMessage, chat: TChat, userId: number | undefined }) => React.ReactNode;
		notification?: (options: {
			message: ChatMessage,
			withRemoteId: boolean | undefined,
			contacts: Array<ChatMessageAccess>,
		}) => React.ReactNode;
	};
	formTag?: (formikBag: FormikProps<NewMessage>, { state, setState, waveColor }) => React.ReactNode;
	messagesHeaderAction?:
		(options: {
			currentChat: TChat,
			user: TUser,
			leaveChat: () => void,
			navigate: NavigateFunction,
			patientId: React.MutableRefObject<number | null>,
			state: { completeChatLoading: boolean },
			completeChat: (chat: TChat) => Promise<void> | undefined,
			withRemoteId: boolean,
			actions: ChatActions<TUser, TChatMessage, TChat>,
			storageName: string,
		}) => React.ReactNode;
	chatMessageAction?: (options: {
		edit: (message: TChatMessage | null) => void,
		remove: () => void,
		isEdit: boolean,
		message: TChatMessage,
		update: (message: TChatMessage) => void,
		fromUser: boolean,
		// plugin options
		options?: BaseParams,
		reply: (message: TChatMessage | null) => void,
	}) => React.ReactNode;
	chatsListHeaderComponent?: (options: {
		filters: BaseParams,
		handleChange: HandleChangeDelegate<BaseParams, BaseParams>,
		selectChat: (chat: TChat | null) => void,
		pageSettings: ChatSettingsChatPageSettings,
		user: TUser,
	}) => React.ReactNode;
	onMessageListClick?: (e, chat: TChat) => void;
	messageAttachment?: (message: TChatMessage) => React.ReactNode;
	messageAttachmentBefore?: (message: TChatMessage) => React.ReactNode;
	notificationHandler?: (
		notification: Notification<BaseUser>,
		storeName: string,
		actions: ChatsActionCreators<TUser, ApplicationStateWithChats<TUser>>,
		options?: BaseParams // plugin options
	) => void;
	sendButtonWrapper?: (button: React.ReactNode, formikBag: FormikProps<NewMessage>, disabled: boolean) => React.ReactNode;
	options?: BaseParams;
}

export type ChatListHeaderSettingsType<
	TUser extends BaseChatUser = BaseChatUser,
	TChat extends Chat<TUser> = Chat<TUser>,
> = Array<ChatPlugins |
	((options: {
		user: TUser,
		selectChat: (chat: TChat | null) => void,
		handleChange: HandleChangeDelegate<any, any>,
		filters: BaseParams,
		pageSettings: ChatSettingsChatPageSettings,
	}) => React.ReactNode)>;

type CustomComponent<
	TUser extends BaseChatUser = BaseChatUser,
	TChatMessage extends ChatMessage<TUser> = ChatMessage<TUser>,
	TChat extends Chat<TUser> = Chat<TUser>,
> = ((options: {
	currentChat: TChat,
	user: TUser,
	leaveChat: () => void,
	navigate: NavigateFunction,
	patientId: React.MutableRefObject<number | null>,
	state: { completeChatLoading: boolean },
	completeChat: (chat: TChat) => Promise<void> | undefined,
	withRemoteId: boolean,
	actions: ChatActions<TUser, TChatMessage, TChat>,
}) => React.ReactNode);

export type ChatMessagesHeaderSettingsType<
	TUser extends BaseChatUser = BaseChatUser,
	TChatMessage extends ChatMessage<TUser> = ChatMessage<TUser>,
	TChat extends Chat<TUser> = Chat<TUser>,
> = Array<ChatPlugins
	| CustomComponent<TUser, TChatMessage, TChat>>;

export type ChatNameType<
	TUser extends BaseChatUser = BaseChatUser,
	TChat extends Chat<TUser> = Chat<TUser>
> = (options: {
	currentChat: TChat,
	user: TUser,
	withRemoteId: boolean,
	patientId: React.MutableRefObject<number | null>,
}) => React.ReactNode;

export interface EmojiReaction {
	id: number;
	userId: number;
	user: BaseChatUser;
	objectId: number;
	object: ChatMessage;
	reaction: string;
	animate?: boolean;
}

export interface BaseChatMessage {
	id: number;
	text: string;
	chatId: number;
	files?: Array<File>;
	loading?: boolean;
}

export interface ChatMessage<TUser extends BaseChatUser = BaseChatUser> {
	id: number;
	text: string;
	chatId: number;
	chat?: Chat;
	userId: number;
	user?: TUser;
	time: number;
	sendingTime: number;
	messageViewers: Array<ChatMessageAccess>;
	viewed: boolean;
	viewLoading?: boolean;
	files: Array<File>;
	emojiReactions: Array<EmojiReaction>;
	chatMessageType: ChatMessageType;
	chatRoomType?: ChatRoomType;
	innerChatMessageId: Nullable<number>;
	innerChatMessage: Nullable<ChatMessage<TUser>>;
	loading?: boolean;
	createdViaNotification?: boolean;
	notificationId?: number;
	reaction?: string;
	attachment?: object;
}

export interface ChatMessageAccess<
	TUser extends BaseChatUser = BaseChatUser,
	TChatMessage extends ChatMessage<TUser> = ChatMessage<TUser>
> {
	chatMessageObject?: TChatMessage;
	id: number;
	message: number;
	time: number;
	user?: BaseChatUser;
	userId: number;
	viewed: boolean;
}

export interface BaseChatUser extends BaseUserWithAvatarWithDeleted {
	remoteId?: number | null;
}

export interface ChatUser<TUser extends BaseChatUser = BaseChatUser> {
	id: number;
	chatId: number;
	userId: number;
	user?: TUser;
	chat?: Chat;
}

export interface NewMessage {
	id: number;
	text: string;
	chatId: number;

	bytes: Array<number>;
	files: Array<FileInterface>;
	attachments: Array<number>;
	chatMessageType: ChatMessageType;
	innerChatMessageId?: number;
	sendingTime?: number;
}

export interface ChatFormButtonsProps extends ButtonsProps {
	chat: Chat;
	isEditMessage?: boolean;
	waveColor?: string;
	setState: React.Dispatch<React.SetStateAction<ChatFormComponentState>>;
	chatId?: number;
	getPopupContainer?: (node: HTMLElement) => HTMLElement;
}

export type ChatFormButtonsWrappers = {
	[key in ChatPlugins]?: (children) => React.ReactNode;
};

export type ChatFormButtonsComponents = ChatPlugins | ((formikBag: FormikProps<NewMessage>, props: ChatFormButtonsProps) => React.ReactNode);

export enum StickerType {
	Image,
	Giphy
}

export type ChatMessageActionsComponent<TUser extends BaseChatUser,
	TChatMessage extends ChatMessage<TUser> = ChatMessage<TUser>> = ChatPlugins
	| (({
		remove,
		update,
		edit,
		isEdit,
		message,
		fromUser,
		options,
	}: {
	remove: () => void,
	update: (message: TChatMessage) => void,
	edit: (message: TChatMessage) => void,
	isEdit: boolean,
	message: TChatMessage,
	fromUser: boolean,
	options,
}) => React.ReactNode);

export type ChatOtherComponents = ChatPlugins | (({
	chat, defaultIdx, setImg, files, basePath,
}) => React.ReactNode);

export interface ChatStickerCollectionItem {
	image: string;
	description?: string;
	fullImage?: string;
}

export interface ChatStickerCollection {
	items: Array<ChatStickerCollectionItem>;
	image: string;
	description?: string;
	enable?: boolean;
}

export interface ChatSettingsRequests {
	chat: string;
	chatUser: string;
	chatMessage: string;
	chatMessageAccess: string;
	typing: string;
	loadChats: string;
	loadMessages: string;
	getChat: string;
	completeChat?: string;
	removeMessage?: string;
	updateMessage?: string;
	chatEmojiReactionMessage?: string;
	videoChat?: string;
	getOrCreatePersonalChat: string;
	readAllChatMessages: string;
}

export interface ChatSettingsNotificationTypes {
	chat: string;
	chatUser: string;
	chatMessage: string;
	chatMessageAccess: string;
	chatReaction: string;
	typing: string;
	updateChatCounterNotification: string;
	updateUserMessagesCountNotification: string;
	addChatFromArchive?: string;
	readAllChatMessagesNotification?: string;
	readChatMessagesNotification?: string;
}

export interface ChatSettingsAvatarSettings<TUser extends BaseChatUser,
	TChatMessage extends ChatMessage<TUser>,
	TChat extends Chat<TUser>> {
	// maybe better return components with special interface
	getUserAvatar: (user: any) => React.ReactNode;
	getUserAvatarAtMention?: (user: any) => React.ReactNode;
	getChatAvatar: (chat: TChat, userId: number) => React.ReactNode;
	notificationAvatar: (state: any) => React.ReactNode;
	viewerAvatar?: (state: any) => React.ReactNode;
}

export interface ChatSettingsFormSettings {
	sendByEnter: boolean;
	underFormLabel: string;
	allowPasteImages: boolean;
	maxAttachmentsCount: number;
	maxMessageLength: number;
	waveColor?: string;
}

export type PluginsDictionary<TUser extends BaseChatUser = BaseChatUser,
	TChatMessage extends ChatMessage<TUser> = ChatMessage<TUser>,
	TChat extends Chat<TUser> = Chat<TUser>,
> = {
	[key in ChatPlugins]?: ChatPlugin<TUser, TChatMessage, TChat>;
};

export interface ChatStoreSettings<TUser extends BaseChatUser,
	TChatMessage extends ChatMessage<TUser> = ChatMessage<TUser>,
	TChat extends Chat<TUser> = Chat<TUser>> {
	getActionCreators: () => ChatActions<TUser, TChatMessage, TChat>;
	getChatStore: (store) => ({ chats: List<TChat>, chatsLoaded: boolean });
	getMessages: (chatId: number) => (state) => (List<TChatMessage> | undefined);
	getCurrentChat: (state) => TChat | null;
	userUnviewedMessagesCountName: string;
}
